import { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import type { ContentDocument, ContentViewModel } from "../../API/Models";
import { getContentById } from "./contentByIdClient";
import { client } from "../../Clients";
import { showSuccessModal } from "../../Helper/showSuccessModal";
import {
	buildDraftFromContentDoc,
	componentsForPayload,
	normalizeComponents,
	normalizeImageFields,
	safeDecodeSlug
} from "./pageEditorUtils";
import { normalizeStorageKey } from "../../Helper/storage";
import { sanitizePageComponentsForSave, sanitizeParagraphHtml } from "./paragraphHtml";
import { componentDefinitionMap } from "../../Pages/pageComponentLibrary";
import type { AdminPage, PageMetadata } from "./types";
import { normalizeLayoutForSave, type PageLayoutSettings } from "./pageLayoutSettings";

const DEFAULT_LANGUAGE = "fr";
const PAGE_SCHEMA_KEY = "page";

export type UsePagePersistenceArgs = {
	/** The canonical id from the route (hash-based). */
	routeId: string | undefined;
	/** The slug from the route params. */
	routeSlug: string | undefined;
	/** Slug from the query string. */
	slugFromQuery: string;
	/** Prefix from the query string. */
	prefix: string;
	/** Initial language resolved from route/query. */
	initialLanguage: string;
	/** Route language param. */
	lang: string | undefined;
	/** Decoded slug value. */
	slug: string | undefined;
	/** Decoded id value. */
	id: string | undefined;
	/** Whether this is the /new-page route. */
	isNewDocument: boolean;
	/** Counter that triggers a reload. */
	reloadCount: number;
	/** Callback to set the draft (with component keys attached). */
	setDraftWithComponents: (next: AdminPage | null) => void;
	/** Current draft. */
	draft: AdminPage | null;
};

export type UsePagePersistenceReturn = {
	view: ContentViewModel | null;
	setView: (v: ContentViewModel | null) => void;
	loadingView: boolean;
	saving: boolean;
	error: string | null;
	setError: (e: string | null) => void;
	message: string | null;
	setMessage: (m: string | null) => void;
	deleting: boolean;
	restoring: boolean;
	isEditing: boolean;
	setIsEditing: (v: boolean) => void;
	loadPageView: (language: string, slugValue: string) => Promise<ContentViewModel | null>;
	persist: () => Promise<void>;
	deployPreview: () => Promise<void>;
	deployingPreview: boolean;
	performDelete: () => Promise<void>;
	handleRestore: () => Promise<void>;
	handleDelete: () => void;
	showDeleteConfirm: boolean;
	setShowDeleteConfirm: (v: boolean) => void;
};

/**
 * Hook that encapsulates all page save/load/delete/restore/deploy logic
 * and the main initialization effect that loads by id/slug/new.
 */
export function usePagePersistence(args: UsePagePersistenceArgs): UsePagePersistenceReturn {
	const {
		routeId,
		routeSlug,
		slugFromQuery,
		prefix,
		initialLanguage,
		lang,
		slug,
		id,
		isNewDocument,
		reloadCount,
		setDraftWithComponents,
		draft
	} = args;

	const history = useHistory();
	const [view, setView] = useState<ContentViewModel | null>(null);
	const [loadingView, setLoadingView] = useState(false);
	const [saving, setSaving] = useState(false);
	const [deployingPreview, setDeployingPreview] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [message, setMessage] = useState<string | null>(null);
	const [deleting, setDeleting] = useState(false);
	const [restoring, setRestoring] = useState(false);
	const [isEditing, setIsEditing] = useState(isNewDocument);
	const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

	const loadPageView = useCallback(
		async (language: string, slugValue: string): Promise<ContentViewModel | null> => {
			const langCode = (language || DEFAULT_LANGUAGE).toLowerCase();
			const normalizedSlug = (safeDecodeSlug(slugValue) ?? slugValue).trim().toLowerCase();
			if (!normalizedSlug) {
				setError("Slug is required to load page content.");
				return null;
			}

			setLoadingView(true);
			setError(null);
			setMessage(null);
			try {
				const response = await client.content.apiContentSchemaKeyLanguageGet(
					PAGE_SCHEMA_KEY,
					langCode,
					normalizedSlug
				);
				const vm: ContentViewModel | null = response.data ?? null;
				setView(vm);
				return vm;
			} catch (err) {
				console.error(err);
				setView(null);
				setError("Unable to load page content.");
				return null;
			} finally {
				setLoadingView(false);
			}
		},
		[]
	);

	// Main initialization effect: load by id/slug/new
	useEffect(() => {
		if (id) {
			(async () => {
				setLoadingView(true);
				setError(null);
				setMessage(null);
				try {
					// Resolve the page directly by id (O(1)) instead of downloading the whole page
					// collection and filtering client-side — the latter pulled ~5 MB and stalled the
					// editor on "Loading page…" for seconds on every open.
					const doc = await getContentById(id);
					if (!doc) {
						setDraftWithComponents(null);
						setView(null);
						setError("The requested page was not found.");
						return;
					}

					const data = (doc.data ?? {}) as any;
					const slugValue = ((data?.slug as string | undefined) ?? "").trim();
					if (!slugValue) {
						setDraftWithComponents(null);
						setView(null);
						setError("The requested page is missing a slug.");
						return;
					}

					const langCode = (
						(data?.language as string | undefined) ??
						doc.language ??
						initialLanguage
					).toLowerCase();

					const vm = await loadPageView(langCode, slugValue);
					const currentDoc = (vm?.current as ContentDocument) ?? doc;
					const nextDraft = buildDraftFromContentDoc(currentDoc, langCode, DEFAULT_LANGUAGE);
					if (!nextDraft) {
						setDraftWithComponents(null);
						setView(null);
						setError("The requested page could not be loaded.");
						return;
					}

					setDraftWithComponents(nextDraft);
					setView(vm);
					setIsEditing(false);
				} catch (err) {
					console.error(err);
					setError("Unable to load the selected page.");
					setDraftWithComponents(null);
					setView(null);
				} finally {
					setLoadingView(false);
				}
			})();
			return;
		}

		const startSlug = slugFromQuery || slug || prefix || "";
		if (!startSlug) {
			setDraftWithComponents({
				slug: "",
				language: initialLanguage,
				title: "",
				metadata: { title: "", description: "" },
				components: []
			});
			setView(null);
			return;
		}

		if (slug) {
			(async () => {
				const langCode = (lang ?? initialLanguage).toLowerCase();
				const vm = await loadPageView(langCode, startSlug);
				if (!vm) {
					setDraftWithComponents({
						id: undefined,
						slug: safeDecodeSlug(startSlug) ?? startSlug,
						language: langCode,
						title: "",
						metadata: { title: "", description: "" },
						components: []
					});
					setView(null);
					return;
				}

			const nextDraft = buildDraftFromContentDoc(vm.current as ContentDocument | undefined, langCode, DEFAULT_LANGUAGE);
				if (!nextDraft) {
					setDraftWithComponents({
						id: undefined,
						slug: safeDecodeSlug(startSlug) ?? startSlug,
						language: langCode,
						title: "",
						dynamicContentTypeId: null,
						dynamicPreviewSlugs: [],
						dynamicProductionSlugs: [],
						metadata: { title: "", description: "" },
						components: []
					});
					setView(null);
					return;
				}

				setDraftWithComponents(nextDraft);
				setView(vm);
				setIsEditing(false);
			})();
			return;
		}

		setDraftWithComponents({
			id: undefined,
			slug: safeDecodeSlug(startSlug) ?? startSlug,
			language: initialLanguage,
			title: "",
			dynamicContentTypeId: null,
			dynamicPreviewSlugs: [],
			dynamicProductionSlugs: [],
			metadata: { title: "", description: "" },
			components: []
		});
		setView(null);
	}, [id, slugFromQuery, slug, prefix, initialLanguage, lang, loadPageView, setDraftWithComponents, reloadCount]);

	const persist = useCallback(async () => {
		if (!draft?.slug || !draft.language) {
			setError("Slug and language are required.");
			return;
		}
		setSaving(true);
		setError(null);
		setMessage(null);
		try {
			const langCode = (draft.language ?? initialLanguage).toLowerCase();
			const normalizedSlug = (draft.slug ?? "").trim().toLowerCase();
			const normalizedComponents = normalizeComponents(draft.components).map((component) => {
				const definition = componentDefinitionMap.get(component.componentType as any) ?? null;
				if (!definition?.fields?.length) return component;
				return normalizeImageFields(component, definition.fields);
			});
			const sanitizedComponents = sanitizePageComponentsForSave(normalizedComponents) as typeof normalizedComponents;
			const rawMetaImage = (draft.metadata as PageMetadata | undefined)?.image;
			const normalizedMetaImage = typeof rawMetaImage === "string" ? normalizeStorageKey(rawMetaImage) : rawMetaImage;
			const metadataBase = { ...(draft.metadata ?? {}) } as PageMetadata;
			delete (metadataBase as { taxonomyIds?: unknown }).taxonomyIds;
			const normalizedTaxonomyIds = Array.isArray(draft.taxonomyIds)
				? draft.taxonomyIds.filter(Boolean)
				: [];
			const normalizedLayout = normalizeLayoutForSave(draft.layout);
			const payloadPage: AdminPage = {
				...draft,
				slug: normalizedSlug,
				language: langCode,
				title: typeof draft.title === "string" ? sanitizeParagraphHtml(draft.title) : "",
				taxonomyIds: normalizedTaxonomyIds,
				metadata: {
					...metadataBase,
					title: sanitizeParagraphHtml((draft.metadata as PageMetadata | undefined)?.title ?? ""),
					description: (draft.metadata as PageMetadata | undefined)?.description ?? "",
					image: normalizedMetaImage
				},
				layout: normalizedLayout,
				components: componentsForPayload(sanitizedComponents) as AdminPage["components"]
			};

			const payloadDoc: ContentDocument = {
				contentType: PAGE_SCHEMA_KEY,
				language: langCode,
				id: draft.id ?? undefined,
				isDisabled: false,
				isDeleted: false,
				data: payloadPage
			};

			const saveResponse = await client.content.apiContentSchemaKeyLanguagePut(
				PAGE_SCHEMA_KEY,
				langCode,
				normalizedSlug,
				payloadDoc
			);
			const nextVm = saveResponse.data ?? null;
			setView(nextVm);

			const savedDoc = nextVm?.current as ContentDocument | undefined;
			const savedData = (savedDoc?.data ?? {}) as any;
			const savedTaxonomyIds = Array.isArray(savedData?.taxonomyIds)
				? (savedData.taxonomyIds as string[]).filter(Boolean)
				: normalizedTaxonomyIds;
			const savedDynamicPreviewSlugs = Array.isArray(savedData?.dynamicPreviewSlugs)
				? (savedData.dynamicPreviewSlugs as string[]).filter(Boolean)
				: payloadPage.dynamicPreviewSlugs ?? [];
			const savedDynamicProductionSlugs = Array.isArray(savedData?.dynamicProductionSlugs)
				? (savedData.dynamicProductionSlugs as string[]).filter(Boolean)
				: payloadPage.dynamicProductionSlugs ?? [];
			const savedDynamicContentTypeId =
				typeof savedData?.dynamicContentTypeId === "string"
					? (savedData.dynamicContentTypeId as string)
					: payloadPage.dynamicContentTypeId ?? null;
			const savedLayoutCandidate = normalizeLayoutForSave(
				(savedData?.layout as PageLayoutSettings | undefined) ?? null
			);
			const savedLayout = savedLayoutCandidate ?? normalizedLayout ?? null;
			const saved: AdminPage = {
				id: savedDoc?.id ?? payloadPage.id ?? draft.id ?? null,
				slug: safeDecodeSlug(savedData?.slug) ?? savedData?.slug ?? payloadPage.slug,
				language: (savedData?.language ?? savedDoc?.language ?? langCode)?.toLowerCase() ?? langCode,
				title: (savedData?.title as string | undefined) ?? payloadPage.title ?? "",
				taxonomyIds: savedTaxonomyIds,
				dynamicContentTypeId: savedDynamicContentTypeId,
				dynamicPreviewSlugs: savedDynamicPreviewSlugs,
				dynamicProductionSlugs: savedDynamicProductionSlugs,
				metadata: (savedData?.metadata as PageMetadata | undefined) ?? payloadPage.metadata ?? {},
				layout: savedLayout,
				components: normalizeComponents(
					(savedData?.components as any[] | undefined) ?? payloadPage.components ?? []
				),
				isDeleted: savedDoc?.isDeleted ?? false,
				previewDeployedAt: savedDoc?.previewDeployedAt ?? null,
				productionDeployedAt: savedDoc?.productionDeployedAt ?? null
			};

			setDraftWithComponents(saved);

			const canonicalId = saved.id ?? draft.id ?? null;

			if (canonicalId && id !== canonicalId) {
				history.replace(`/page/${encodeURIComponent(canonicalId)}`);
			}
			const successMessage = "Page saved.";
			setMessage(successMessage);
			const cur = nextVm?.current as ContentDocument | undefined;
			const previewOutOfSync =
				!!(cur?.updatedAt && cur?.previewDeployedAt && new Date(cur.updatedAt) > new Date(cur.previewDeployedAt));
			showSuccessModal({
				title: "Page saved",
				message: successMessage,
				note: previewOutOfSync ? "Deploy to preview to see changes live." : undefined
			});
		} catch (err: any) {
			console.error(err);
			const apiMessage = err?.response?.data?.error as string;
			setError(apiMessage ?? "Failed to save the page.");
		} finally {
			setSaving(false);
		}
	}, [draft, id, initialLanguage, setDraftWithComponents, history]);

	const deployPreview = useCallback(async () => {
		if (!draft?.slug || !draft.language) return;
		setDeployingPreview(true);
		setError(null);
		setMessage(null);
		try {
			const langCode = (draft.language ?? initialLanguage).toLowerCase();
			const normalizedSlug = (draft.slug ?? "").trim().toLowerCase();
			await client.deployment.apiDeploymentDeploymentQueueDeployPost("preview", {
				withCredentials: true,
				headers: { Accept: "application/json" }
			});
			const vm = await loadPageView(langCode, normalizedSlug);
			const nextDraft = buildDraftFromContentDoc(vm?.current as ContentDocument | undefined, langCode, DEFAULT_LANGUAGE);
			if (vm && nextDraft) {
				setDraftWithComponents(nextDraft);
				setView(vm);
			}
			const successMessage = "Preview deployed.";
			setMessage(successMessage);
			showSuccessModal({
				title: "Preview deploy complete",
				message: successMessage
			});
		} catch (err) {
			console.error(err);
			setError("Failed to deploy preview.");
		} finally {
			setDeployingPreview(false);
		}
	}, [draft?.slug, draft?.language, initialLanguage, loadPageView, setDraftWithComponents]);

	const performDelete = useCallback(async () => {
		if (!draft?.id || !draft.slug || !draft.language) return;
		setShowDeleteConfirm(false);
		setDeleting(true);
		setError(null);
		setMessage(null);
		try {
			const langCode = (draft.language ?? initialLanguage).toLowerCase();
			const normalizedSlug = (draft.slug ?? "").trim().toLowerCase();

			const normalizedForDelete = normalizeComponents(draft.components);
			const deleteComponents = componentsForPayload(
				sanitizePageComponentsForSave(normalizedForDelete) as typeof normalizedForDelete
			) as AdminPage["components"];
			const payloadPage: AdminPage = {
				...draft,
				slug: normalizedSlug,
				language: langCode,
				id: draft.id,
				isDeleted: true,
				metadata: {
					...(draft.metadata ?? {}),
					title: sanitizeParagraphHtml(draft.metadata?.title ?? draft.title ?? "")
				},
				components: deleteComponents
			};

			const payloadDoc: ContentDocument = {
				contentType: PAGE_SCHEMA_KEY,
				language: langCode,
				id: draft.id ?? undefined,
				isDisabled: false,
				isDeleted: true,
				data: payloadPage
			};

			await client.content.apiContentSchemaKeyLanguagePut(
				PAGE_SCHEMA_KEY,
				langCode,
				normalizedSlug,
				payloadDoc
			);

			const pageLabel = draft.title?.trim() || draft.slug || "Page";
			showSuccessModal({
				title: "Page deleted",
				message: `${pageLabel} has been deleted.`
			});
			history.push("/pages");
		} catch (err: any) {
			console.error(err);
			const apiMessage = err?.response?.data?.error as string;
			setError(apiMessage ?? "Failed to delete the page.");
		} finally {
			setDeleting(false);
		}
	}, [draft, initialLanguage, history]);

	const handleDelete = useCallback(() => {
		if (!draft?.id || !draft.slug || !draft.language) return;
		setShowDeleteConfirm(true);
	}, [draft?.id, draft?.slug, draft?.language]);

	const handleRestore = useCallback(async () => {
		if (!draft?.id?.trim()) return;
		const pageId = draft.id.trim();
		setRestoring(true);
		setError(null);
		setMessage(null);
		try {
			await client.content.apiContentPageRestorePost(pageId, { withCredentials: true });
			const slug = (draft.slug ?? "").trim();
			const langCode = (draft.language ?? initialLanguage).toLowerCase();
			const response = await client.content.apiContentSchemaKeyLanguageGet(
				PAGE_SCHEMA_KEY,
				langCode,
				slug
			);
			const vm: ContentViewModel | null = response.data ?? null;
			if (vm?.current) {
				setView(vm);
				const nextDraft = buildDraftFromContentDoc(vm.current as ContentDocument, langCode, DEFAULT_LANGUAGE);
				if (nextDraft) setDraftWithComponents(nextDraft);
			}
			showSuccessModal({
				title: "Page restored",
				message: "This page has been restored. You can publish it to preview again."
			});
		} catch (err: any) {
			console.error(err);
			const apiMessage = err?.response?.data?.message ?? err?.response?.data ?? err?.message ?? "Restore failed.";
			setError(String(apiMessage));
		} finally {
			setRestoring(false);
		}
	}, [draft?.id, draft?.slug, draft?.language, initialLanguage, setDraftWithComponents]);

	return {
		view,
		setView,
		loadingView,
		saving,
		error,
		setError,
		message,
		setMessage,
		deleting,
		restoring,
		isEditing,
		setIsEditing,
		loadPageView,
		persist,
		deployPreview,
		deployingPreview,
		performDelete,
		handleRestore,
		handleDelete,
		showDeleteConfirm,
		setShowDeleteConfirm
	};
}
